import React, { useEffect, useState } from "react";
import {
    Box,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    TextField,
    Typography,
    CircularProgress,
    Alert,
    Chip,
} from "@mui/material";
import { WarningAmber, LocalShipping, Search } from "@mui/icons-material";
import { issueAlertList } from "../../../Services/Services";

export default function IssueAlert() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [truckCount, setTruckCount] = useState(0);
    const [alertCount, setAlertCount] = useState(0);
    const [search, setSearch] = useState({
        truck_no: "",
    });

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await issueAlertList();

            setRows(response.results || []);
            setTruckCount(response.truck_count || 0);
            setAlertCount(response.total_alerts || 0);
        } catch (err) {
            setError("Failed to load issue alerts. Please try again.");
        }
        setLoading(false);
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleSearchChange = (e) => {
        const { name, value } = e.target;
        setSearch((prev) => ({
            ...prev,
            [name]: value.toLowerCase(),
        }));
    };

    // Filter data based on search
    const filteredRows = rows.filter((truck) => {
        return (
            search.truck_no === "" ||
            (truck.truck_no && truck.truck_no.toLowerCase().includes(search.truck_no))
        );
    });

    // Flatten alerts for table display
    const flattenedData = [];
    filteredRows.forEach((truck, index) => {
        if (truck.alerts && truck.alerts.length > 0) {
            truck.alerts.forEach((alert, alertIndex) => {
                flattenedData.push({
                    id: `${truck.truck_no}-${alertIndex}`,
                    srNo: alertIndex === 0 ? index + 1 : null,
                    truck_no: truck.truck_no,
                    date: alert.date,
                    time: alert.time,
                    isFirst: alertIndex === 0,
                    totalAlerts: truck.alerts.length,
                    alertCount: truck.alert_count,
                });
            });
        }
    });

    const totalAlerts = flattenedData.length;
    const totalTrucks = filteredRows.length;

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" } }}>
            {/* Header with Stats */}
            <Box sx={{ display: "flex", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Issue Alert History
                </Typography>
                <Chip
                    icon={<LocalShipping />}
                    label={`${truckCount} Trucks`}
                    size="small"
                    sx={{ ml: 1 }}
                />
                <Chip
                    icon={<WarningAmber />}
                    label={`${alertCount} Alerts (Last 7 Days)`}
                    size="small"
                    color="warning"
                />
            </Box>

            {/* Search */}
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <TextField
                    label="Search Truck No"
                    name="truck_no"
                    value={search.truck_no}
                    onChange={handleSearchChange}
                    size="small"
                    sx={{ width: 200 }}
                    InputProps={{
                        startAdornment: <Search sx={{ mr: 1, fontSize: 20, color: "text.secondary" }} />,
                    }}
                />
            </Box>

            {/* Table */}
            <TableContainer
                sx={{
                    overflowX: "auto",
                    overflowY: "auto",
                    maxHeight: "650px",
                    mt: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                }}
            >
                <Table
                    size="small"
                    sx={{
                        tableLayout: "auto",
                        minWidth: "100%",
                        "& td, & th": {
                            textAlign: "center",
                            whiteSpace: "nowrap",
                            padding: "6px 12px",
                            fontSize: "0.8125rem",
                            borderBottom: "1px solid #e0e0e0",
                        },
                    }}
                >
                    <TableHead
                        sx={{
                            "& th": {
                                fontWeight: "bold",
                                backgroundColor: "#f5f5f5",
                                position: "sticky",
                                top: 0,
                                zIndex: 1,
                            },
                        }}
                    >
                        <TableRow>
                            <TableCell sx={{ width: "80px" }}>Sr. No.</TableCell>
                            <TableCell sx={{ minWidth: "140px" }}>Truck No</TableCell>
                            <TableCell sx={{ minWidth: "120px" }}>
                                Alert Count
                            </TableCell>
                            <TableCell sx={{ minWidth: "130px" }}>Date</TableCell>
                            <TableCell sx={{ minWidth: "130px" }}>Time</TableCell>
                            
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {flattenedData.length > 0 ? (
                            flattenedData.map((item, index) => (
                                <TableRow
                                    key={item.id}
                                    sx={{
                                        backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                                        "&:hover": { backgroundColor: "#f0f0f0" },
                                    }}
                                >
                                   {item.isFirst ? (
    <>
        <TableCell
            rowSpan={item.totalAlerts}
            sx={{ verticalAlign: "middle", fontWeight: 500 }}
        >
            {item.srNo}
        </TableCell>

        <TableCell
            rowSpan={item.totalAlerts}
            sx={{ verticalAlign: "middle", fontWeight: 500 }}
        >
            {item.truck_no}
        </TableCell>

        <TableCell
            rowSpan={item.totalAlerts}
            sx={{
                verticalAlign: "middle",
                fontWeight: "bold",
                color: "red",
            }}
        >
            {item.alertCount}
        </TableCell>
    </>
) : null}

<TableCell>{item.date}</TableCell>

<TableCell>{item.time}</TableCell>
                                    
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">
                                        No issue alerts found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Footer */}
            {flattenedData.length > 0 && (
                <Box
                    sx={{
                        mt: 2,
                        display: "flex",
                        justifyContent: "flex-end",
                    }}
                >
                    <Typography variant="caption" color="text.secondary">
                        Last updated: {new Date().toLocaleString()}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}